import { useCallback, useEffect } from 'react';
import { compact } from 'lodash';
import { useEnabledFeatures } from './enabled-features/use-enabled-features';
import {
    AttributeOption,
    CallForServiceOption,
    EventLabelOption,
    EventPanelFormType,
    OriAliasOptionDto,
} from '@mark43/cad-api';
import {
    asCallForServiceOption,
    asEventLabelOption,
    asPriorityOption,
} from '../../event-panel/event-panel-form/helpers/prefill-helpers';
import { useUnitAssignment } from '../../unit-assignment/unit-assignment-context';
import { useAgencyTypeGlobalAttributeId } from './use-agency-type-global-attribute-id';
import { useGetAgencyType } from './use-get-agency-type';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useSelectedLocation } from '../../event-panel/tabs/hooks/use-selected-location';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { getPriorityPrefill } from '../helpers/form-prefill';
import { useFormContext } from 'react-hook-form';

const FIELD_NAMES = ['additionalCallForServices', 'callForService', 'eventLabels'];

export function usePriorityPrefill(priorityOptions: AttributeOption[]): void {
    const { form } = useIntegratedForm<EventPanelFormType>();

    const setPriorityPrefill = useCallback(
        (data, name) => {
            const callForService = asCallForServiceOption(data.callForService);
            const additionalCallForServices = compact(
                (data.additionalCallForServices ?? []).map(asCallForServiceOption)
            );
            const eventLabels = compact((data.eventLabels ?? []).map(asEventLabelOption));
            const currentPriority =
                name === 'eventLabels' ? asPriorityOption(data.priority) : undefined;
            const priorityPrefill = getPriorityPrefill(
                currentPriority,
                callForService,
                additionalCallForServices as CallForServiceOption[],
                eventLabels as EventLabelOption[],
                priorityOptions
            );

            if (priorityPrefill) {
                form.setValue('priority', priorityPrefill, { shouldDirty: true });
            } else {
                form.resetField('priority');
            }
        },
        [form, priorityOptions]
    );

    useEffect(() => {
        const subscription = form.watch((data, { name, type }) => {
            if (type === 'change' && FIELD_NAMES.includes(name as string)) {
                setPriorityPrefill(data, name);
            }
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [form, priorityOptions, setPriorityPrefill]);
}

export function useRiskLevelPrefill(riskLevelOptions: AttributeOption[]): void {
    const { form } = useIntegratedForm<EventPanelFormType>();
    const { eventsHaveRiskLevel } = useEnabledFeatures();

    useEffect(() => {
        const subscription = form.watch((data, { name, type }) => {
            if (name !== 'callForService' || type !== 'change') {
                return;
            }

            const riskLevelId = data.callForService?.riskLevelAttrId;
            const riskLevelOption = riskLevelOptions.find((option) => option.value === riskLevelId);

            if (riskLevelOption && eventsHaveRiskLevel) {
                form.setValue('riskLevel', riskLevelOption, { shouldDirty: true });
            } else {
                form.resetField('riskLevel');
            }
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [form, riskLevelOptions, eventsHaveRiskLevel]);
}

export function useAgencyPrefill(isCreateEventForm: boolean): void {
    const form = useFormContext<EventPanelFormType>();
    const unitAssignment = useUnitAssignment();
    const agencyTypeGlobalAttrId = useAgencyTypeGlobalAttributeId();
    const getAgencyType = useGetAgencyType(agencyTypeGlobalAttrId);
    const { eventPanelOptions } = useBootstrapState();

    useEffect(() => {
        if (!isCreateEventForm) return;

        const agencyOptions = eventPanelOptions?.[`${getAgencyType()}AgencyProfileOptions`] ?? [];
        const prefillAgencyOption = agencyOptions.find(
            (option) => option.value === unitAssignment?.unitAgencyId
        );

        if (prefillAgencyOption) {
            form.setValue(`${getAgencyType()}Agency`, prefillAgencyOption);
        }
    }, [isCreateEventForm, unitAssignment?.unitAgencyId, eventPanelOptions, getAgencyType, form]);
}

export function useOriAliasPrefill(oriAliasOptions: OriAliasOptionDto[]): void {
    const { form } = useIntegratedForm<EventPanelFormType>();

    useEffect(() => {
        if (oriAliasOptions.length) {
            const defaultOption = oriAliasOptions.find((option) => option.isDefault);
            form.setValue('oriAliasOption', defaultOption ?? null);
        }
    }, [form, oriAliasOptions]);
}

export function useReportingPartyLocationPrefill(): void {
    const { form } = useIntegratedForm<EventPanelFormType>();
    const selectedLocation = useSelectedLocation();
    const { eventLocationPrefillsReportingPartyLocation } = useEnabledFeatures();
    const { getValues, setValue } = form;

    useEffect(() => {
        const isCreatingEvent = !getValues('eventId');
        const reportingPartyLocation = getValues('reportingParties.0.location');

        if (
            eventLocationPrefillsReportingPartyLocation &&
            isCreatingEvent &&
            selectedLocation &&
            !reportingPartyLocation
        ) {
            setValue('reportingParties.0.location', selectedLocation);
        }
    }, [selectedLocation, getValues, setValue, eventLocationPrefillsReportingPartyLocation]);
}
